import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { NoPrint } from 'react-easy-print';
import { pick, pathOr, isNil } from 'ramda';
import moment from 'moment';

// Hooks
import { useFetchAffordableConfigs } from './hooks';
import { useFetchPassbookRatesByState } from '../../ViewProperty/AffordableSetup/hooks';
import useSelectProperty from '../../../hooks/useSelectedProperty';

// Redux Form
import { reduxForm, formValueSelector } from 'redux-form';

// Components
import { CheckBox } from '../SharedFormFields';
import { AssetFooter } from './footer/AssetFooter';
import { AltAssetFooter } from './footer/AltAssetFooter';
import { AssetTableHeader } from './header/AssetTableHeader';
import { AltAssetTableHeader } from './header/AltAssetTableHeader';
import { Spinner } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  Banner,
  Grid,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';

// Types
import type { AssetFormProps } from '../types';

// Utils
import {
  getAssetDefinitions,
  getInitialAssetFormReviewed,
  getPassbookRateByProgramAndDate,
  requiresAssetConfirmation,
} from './utils';
import validate from './validate';
import {
  getAssetAlternateFormRulesByState,
  hasStateSpecificAssetFormLayout,
} from './stateRules';

// Constants
import {
  FORM_INFO,
  ASSET_ROW_NAMES,
  ASSET_VALUE_FIELDS,
  ASSET_INCOME_FIELDS,
  NEW_ALTERNATE_ROW,
  ALL_ASSETS_DEFINITIONS,
} from './constants.js';

import componentMessages from './messages';
import generalMessages from '../../App/messages';
import { getAssetThreshold } from '../../../utils/affordable';
import { AlternateAssetFieldRow } from './assetField/AlternateAssetFieldRow';

const messages = {
  ...generalMessages,
  ...componentMessages,
};

const createNewRow = ({ fields, fieldLimit, additionalValues }) => {
  if (fields.length >= fieldLimit) {
    return;
  }
  const newRowData = { ...NEW_ALTERNATE_ROW, ...additionalValues };
  fields.push(newRowData);
};

const generateNoClicked = (changeCallback: Function) => {
  return (rowName: string) => {
    changeCallback(`${rowName}AssetValue`, '0.00');
    changeCallback(`${rowName}AssetIncome`, '0.00');
    changeCallback(`${rowName}InterestRate`, '0.00');
    if (rowName === 'otherRetirementAccount') {
      changeCallback('otherRetirementAccountName', null);
    }
    if (rowName === 'other') {
      changeCallback('otherAssetName', null);
    }
    if (rowName === 'disposed') {
      changeCallback('disposedDate', null);
    }
  };
};

// handle logic for clicking no for alternate asset form
const generateAltNoClicked = (changeCallback: Function) => {
  return (rowName: string, isList = false) => {
    if (isList) {
      const clearedList = [{ ...NEW_ALTERNATE_ROW, otherAssetType: 'default' }];
      changeCallback(`${rowName}List`, clearedList);
    } else {
      changeCallback(`${rowName}AssetMarketValue`, '0.00');
      changeCallback(`${rowName}AssetInterestRate`, '0.00');
      changeCallback(`${rowName}AssetFeesToConvertCash`, '0.00');
      changeCallback(`${rowName}AssetCashValue`, '0.00');
      changeCallback(`${rowName}AssetAnnualIncome`, '0.00');
      changeCallback(`${rowName}AssetActualImpute`, '-');
      changeCallback(`${rowName}AssetVerification`, -1);
      changeCallback(`${rowName}AssetBankSource`, '');
      changeCallback(`${rowName}AssetAccountNumber`, '');
      if (rowName === 'otherRetirementAccount') {
        changeCallback('otherRetirementAccountName', null);
      }
      if (rowName === 'other') {
        changeCallback('otherAssetName', null);
      }
      if (rowName === 'disposed') {
        changeCallback('disposedDate', null);
      }
    }
  };
};

const generateCalcCashValueFunc = (changeCallback: Function, props) => {
  return (rowName: string, isList = false, index = 0) => {
    if (!isList) {
      const marketValue = parseFloat(props?.[`${rowName}AssetMarketValue`]);
      const feesConvertCash = parseFloat(
        props?.[`${rowName}AssetFeesToConvertCash`],
      );
      if (isNaN(marketValue) || isNaN(feesConvertCash)) {
        changeCallback(`${rowName}AssetCashValue`, '0.00');
      }
      const cashValue = (marketValue - feesConvertCash).toFixed(2);
      changeCallback(`${rowName}AssetCashValue`, cashValue);
    } else {
      // Handle expandable list logic
      const listKey = `${rowName}List`;
      const mv = props?.[listKey]?.[index]?.marketValue ?? null;
      const fees = props?.[listKey]?.[index]?.feesToConvertCash ?? null;
      const marketValue = parseFloat(mv);
      const feesConvertCash = parseFloat(fees);
      if (isNaN(marketValue) || isNaN(feesConvertCash)) {
        changeCallback(`${rowName}List[${index}].cashValue`, '0.00');
      }
      const cashValue = (marketValue - feesConvertCash).toFixed(2);
      changeCallback(`${rowName}List[${index}].cashValue`, cashValue);
    }
  };
};

const generateCalcAnnualIncomeFunc = (changeCallback: Function, props) => {
  const { isHotmaActive = false } = props;
  return (rowName: string, isList = false, index = 0) => {
    if (!isList) {
      const marketValue = parseFloat(props?.[`${rowName}AssetMarketValue`]);
      const interestRate = parseFloat(props?.[`${rowName}AssetInterestRate`]);
      if (isNaN(marketValue) || isNaN(interestRate)) {
        changeCallback(`${rowName}AssetAnnualIncome`, '0.00');
      }
      const annualIncome = ((marketValue * interestRate) / 100).toFixed(2);
      if (isHotmaActive) {
        const currentValue = props?.[`${rowName}AssetActualImpute`];
        const isAnnualIncomePositive = +annualIncome > 0;
        if (isAnnualIncomePositive && currentValue !== 'actual') {
          changeCallback(`${rowName}AssetActualImpute`, 'actual');
        }
      }

      changeCallback(`${rowName}AssetAnnualIncome`, annualIncome);
    } else {
      // Handle expandable list logic
      const listKey = `${rowName}List`;
      const mv = props?.[listKey]?.[index]?.marketValue ?? null;
      const ir = props?.[listKey]?.[index]?.interestRate ?? null;
      const marketValue = parseFloat(mv);
      const interestRate = parseFloat(ir);
      if (isNaN(marketValue) || isNaN(interestRate)) {
        changeCallback(`${rowName}List[${index}].annualIncome`, '0.00');
      }
      const annualIncome = ((marketValue * interestRate) / 100).toFixed(2);
      if (isHotmaActive) {
        const currentValue = props?.[listKey]?.[index]?.actualImpute;
        const isAnnualIncomePositive = +annualIncome > 0;
        if (isAnnualIncomePositive && currentValue !== 'actual') {
          changeCallback(`${rowName}List[${index}].actualImpute`, 'actual');
        }
      }
      changeCallback(`${rowName}List[${index}].annualIncome`, annualIncome);
    }
  };
};

const calculateTotalTaxRefund = (
  data,
  isHotmaActive = false,
  isCertAfter2025,
): number => {
  // Tax Refund is defined as list so no reason to have logic for non-expandable assets
  const taxRefundList = data?.federalTaxRefundOrCreditList;
  return isHotmaActive && isCertAfter2025
    ? (data?.federalTaxRefundOrCredit === 'yes'
        ? taxRefundList ?? []
        : []
      ).reduce((prevTotal, taxRefund) => {
        const value = +taxRefund?.marketValue > 0 ? +taxRefund?.marketValue : 0;
        return value + prevTotal;
      }, 0)
    : 0;
};

const calculateTotalAssets = (
  data,
  isAltForm = false,
  isHotmaActive = false,
  isCertAfter2025,
) => {
  if (isAltForm) {
    const assetDefinitions = getAssetDefinitions(isHotmaActive);
    return assetDefinitions.reduce((prevTotal, assetDef) => {
      if (assetDef.expandable) {
        const key = `${assetDef.name}List`;
        const isFedTaxRefund =
          isCertAfter2025 && assetDef.name === 'federalTaxRefundOrCredit';
        if (!data[key] || data[key].length === 0) {
          return prevTotal;
        } else {
          const totalFromThisAssetList = data[key].reduce((prevTotal, row) => {
            const value =
              +row?.marketValue > 0 && !isFedTaxRefund ? +row?.marketValue : 0;
            const tot = value + prevTotal;
            return tot;
          }, 0);
          return prevTotal + totalFromThisAssetList;
        }
      } else {
        const key = `${assetDef.name}AssetMarketValue`;
        const tot = +data[key] > 0 ? +data[key] + prevTotal : prevTotal;
        return tot;
      }
    }, 0);
  } else {
    return ASSET_VALUE_FIELDS.reduce((prevTotal, key) => {
      const amount = +data[key] > 0 ? +data[key] + prevTotal : prevTotal;
      return amount;
    }, 0);
  }
};

const calculateCashValue = (data, isHotmaActive, isCertAfter2025) => {
  const assetDefinitions = getAssetDefinitions(isHotmaActive);
  return assetDefinitions.reduce((prevTotal, assetDef) => {
    if (assetDef.expandable) {
      const key = `${assetDef.name}List`;
      const isFedTaxRefund =
        isCertAfter2025 && assetDef.name === 'federalTaxRefundOrCredit';
      if (!data[key] || data[key].length === 0) {
        return prevTotal;
      } else {
        const totalFromThisAssetList = data[key].reduce(
          (innerPrevTotal, row) => {
            const tot =
              +row?.cashValue > 0 && !isFedTaxRefund
                ? +row?.cashValue + innerPrevTotal
                : innerPrevTotal;
            return tot;
          },
          0,
        );
        return prevTotal + totalFromThisAssetList;
      }
    } else {
      const key = `${assetDef.name}AssetCashValue`;
      const tot = +data[key] > 0 ? +data[key] + prevTotal : prevTotal;
      return tot;
    }
  }, 0);
};

// Returns total cash value of all assets marked as Impute
const calculateImputeCashValue = (
  data: Object,
  isHotmaActive: boolean,
  isCertAfter2025: boolean,
) => {
  const assetDefinitions = getAssetDefinitions(isHotmaActive);
  return assetDefinitions.reduce((prevTotal, assetDef) => {
    if (assetDef.expandable) {
      const isFedTaxRefund =
        isCertAfter2025 && assetDef.name === 'federalTaxRefundOrCredit';
      const key = `${assetDef.name}List`;
      if (!data[key] || data[key].length === 0) {
        return prevTotal;
      } else {
        const totalFromThisAssetList = data[key].reduce(
          (innerPrevTotal, row) => {
            const isImputed = row?.actualImpute === 'impute';
            const tot =
              +row?.cashValue > 0 && !isFedTaxRefund && isImputed
                ? +row?.cashValue + innerPrevTotal
                : innerPrevTotal;
            return tot;
          },
          0,
        );
        return prevTotal + totalFromThisAssetList;
      }
    } else {
      const key = `${assetDef.name}AssetCashValue`;
      const tot = +data[key] > 0 ? +data[key] + prevTotal : prevTotal;
      return tot;
    }
  }, 0);
};

// Monthly for regular form, annual for alternate form
const calculateTotalAssetIncome = (
  data: Object,
  isAltForm: boolean,
  isHotmaActive: boolean,
  isCertAfter2025: boolean,
) => {
  if (isAltForm) {
    const assetDefinitions = getAssetDefinitions(isHotmaActive);
    return assetDefinitions.reduce((prevTotal, assetDef) => {
      if (assetDef.expandable) {
        const key = `${assetDef.name}List`;
        if (!data[key] || data[key].length === 0) {
          return prevTotal;
        } else {
          const totalFromThisAssetList = data[key].reduce(
            (localPrevTotal, row) => {
              const tot =
                +row?.annualIncome > 0
                  ? +row?.annualIncome + localPrevTotal
                  : localPrevTotal;
              return tot;
            },
            0,
          );
          return prevTotal + totalFromThisAssetList;
        }
      } else {
        const key = `${assetDef.name}AssetAnnualIncome`;
        const tot = +data[key] > 0 ? +data[key] + prevTotal : prevTotal;
        return tot;
      }
    }, 0);
  } else {
    return ASSET_INCOME_FIELDS.reduce((prevTotal, key) => {
      const amount = +data[key] > 0 ? +data[key] + prevTotal : prevTotal;
      return amount;
    }, 0);
  }
};

const calculateImputedIncome = (
  data: Object,
  affordableConfigs: {
    assetThreshold: number,
    passbookRate: number,
  },
  isAltForm: boolean,
  isHotmaActive: boolean,
  isCertAfter2025: boolean,
) => {
  const { assetThreshold, passbookRate } = affordableConfigs;
  if (isAltForm) {
    const totalCashValue = isHotmaActive
      ? calculateImputeCashValue(data, isHotmaActive, isCertAfter2025)
      : calculateCashValue(data, isHotmaActive, isCertAfter2025);
    return +totalCashValue < assetThreshold ? 0 : totalCashValue * passbookRate;
  } else {
    const totalAssets = calculateTotalAssets(
      data,
      isAltForm,
      isHotmaActive,
      isCertAfter2025,
    );
    return +totalAssets < assetThreshold ? 0 : totalAssets * passbookRate;
  }
};

const AssetInformation = (props: AssetFormProps) => {
  const {
    change,
    handleSubmit,
    applicantName,
    onSubmit,
    valid,
    isPrior,
    hasCompliancePermission,
    intl,
    isSubmitting,
    isPrinting,
    handlePrintIncomeAssetForm,
    isHotmaActive = false,
    isAltForm,
    isFormDisabled,
    initialValues: { assetConfirmation },
    wasFormEdited,
    flags,
    effectiveDate,
    programName,
    allowReopenedEdit,
    autoRolloverIncomeAndAssets,
  } = props;
  const selectedProperty = useSelectProperty();
  const {
    hotmaChanges = false,
    assetsTaxRefund,
    requireIncomeAssetConfirmation = false,
  } = flags ?? {};
  const stateRules = getAssetAlternateFormRulesByState(
    selectedProperty?.physicalState,
    programName,
    flags,
    isHotmaActive,
  );
  const hasAlternateColumns =
    isAltForm ||
    hasStateSpecificAssetFormLayout(
      selectedProperty?.physicalState,
      programName,
      flags,
      isHotmaActive,
    );
  const [initialAssets, setInitialAssets] = useState([]);
  const [retirementAccountsAssets, setRetirementAccountsAssets] = useState([]);
  const [lifeInsuranceAssets, setLifeInsuranceAssets] = useState([]);
  const [finalAssets, setFinalAssets] = useState([]);
  const [isAssetFormReviewed, setIsAssetFormReviewed] = useState(false);
  // Disables Save the instant it's clicked, independent of when Redux's
  // isSubmitting/navigation actually resolve (avoids a re-enable flicker if
  // those two updates land in separate renders). Only clears if the form is
  // still mounted after isSubmitting resolves — i.e. the save failed and we
  // didn't navigate away; on success the component unmounts first.
  const [hasSubmitted, setHasSubmitted] = useState(false);
  useEffect(() => {
    if (!isSubmitting && hasSubmitted) {
      const timer = setTimeout(() => setHasSubmitted(false), 0);
      return () => clearTimeout(timer);
    }
  }, [isSubmitting, hasSubmitted]);

  const { data: affordableConfigs } = useFetchAffordableConfigs({
    organizationId: selectedProperty?.organizationId,
    propertyId: selectedProperty?.id,
    effectiveDate,
  });

  const { data: statePassbookRates } = useFetchPassbookRatesByState({
    organizationId: selectedProperty?.organizationId,
    propertyId: selectedProperty?.id,
    state: selectedProperty?.physicalState,
  });

  const assetThreshold = useMemo(() => {
    return getAssetThreshold(affordableConfigs, programName, isHotmaActive);
  }, [affordableConfigs, programName, isHotmaActive]);

  const passbookRate = useMemo(() => {
    return getPassbookRateByProgramAndDate(
      statePassbookRates,
      programName,
      effectiveDate,
    );
  }, [effectiveDate, programName, statePassbookRates]);

  const isCertAfter2025 = (
    isNil(effectiveDate) ? moment.utc() : moment.utc(effectiveDate)
  ).isSameOrAfter(moment.utc('2025-01-01').startOf('day'));

  const isBankDataRequired =
    isHotmaActive && selectedProperty?.physicalState === 'US-CA';

  useEffect(() => {
    if (isAltForm !== hasAlternateColumns) {
      change('isAltForm', hasAlternateColumns);
    }
  }, [change, hasAlternateColumns, isAltForm]);

  useEffect(() => {
    setIsAssetFormReviewed(
      getInitialAssetFormReviewed({
        isHotmaActive,
        autoRolloverIncomeAndAssets,
        requireIncomeAssetConfirmation,
        assetConfirmation,
        wasFormEdited,
      }),
    );
  }, [
    isHotmaActive,
    autoRolloverIncomeAndAssets,
    requireIncomeAssetConfirmation,
    assetConfirmation,
    wasFormEdited,
  ]);

  useEffect(() => {
    const finalAssets = FORM_INFO.finalAssets.filter((asset) =>
      assetsTaxRefund && isHotmaActive && isCertAfter2025
        ? true
        : asset.name !== 'federalTaxRefundOrCredit',
    );

    setInitialAssets(FORM_INFO.initialAssets);
    setRetirementAccountsAssets(FORM_INFO.retirementAccounts);
    setLifeInsuranceAssets(FORM_INFO.lifeInsurance);
    setFinalAssets(finalAssets);
  }, [assetsTaxRefund, isHotmaActive, isCertAfter2025]);
  // $FlowFixMe
  const fields = pick(ASSET_ROW_NAMES, props);
  const fieldsAnnualIncome = ALL_ASSETS_DEFINITIONS.reduce((acc, asset) => {
    const { name, expandable } = asset;
    if (expandable) {
      const key = `${name}List`;
      const list = props[key];
      if (list) {
        const values = list.map((row) => row?.annualIncome);
        return {
          ...acc,
          [name]: {
            expandable,
            values,
          },
        };
      }
      return acc;
    } else {
      const key = `${name}AssetAnnualIncome`;
      return {
        ...acc,
        [name]: props[key],
      };
    }
  }, {});

  const totalMarketAssets = calculateTotalAssets(
    props,
    hasAlternateColumns,
    isHotmaActive,
    isCertAfter2025,
  );

  const totalCashAssets = calculateCashValue(
    props,
    hasAlternateColumns,
    isHotmaActive,
    isCertAfter2025,
  );
  const totalTaxRefund = assetsTaxRefund
    ? calculateTotalTaxRefund(props, isHotmaActive, isCertAfter2025)
    : 0;

  const totalCashValue = Math.max(0, totalCashAssets - totalTaxRefund);

  const totalAssetIncome = calculateTotalAssetIncome(
    props,
    hasAlternateColumns,
    isHotmaActive,
    isCertAfter2025,
  );
  const imputedIncome = calculateImputedIncome(
    props,
    {
      assetThreshold,
      passbookRate,
    },
    hasAlternateColumns,
    isHotmaActive,
    isCertAfter2025,
  );
  // $FlowFixMe
  const stateDisabled = pathOr(
    false,
    ['location', 'state', 'isDisabled'],
    props,
  );
  const isDisabled =
    stateDisabled && !hasCompliancePermission && !allowReopenedEdit;
  const priorAndNotCompliance = hasAlternateColumns
    ? isPrior
    : isPrior && !hasCompliancePermission;
  const totalMonthlyAssetIncome = hasAlternateColumns
    ? totalAssetIncome / 12
    : totalAssetIncome;
  const submit = (values: Object) => {
    setHasSubmitted(true);
    onSubmit(
      { ...values, isAltForm: hasAlternateColumns },
      totalMonthlyAssetIncome.toFixed(2),
      imputedIncome.toFixed(2),
    );
  };

  const printAssetForm = () => {
    handlePrintIncomeAssetForm('asset');
  };

  // Type of asset, Annual Income and Actual/Impute
  const largeInputColSize = isBankDataRequired ? 1 : 2;
  // Market Value, Fees and Cash Value
  const mediumInputColSize = isHotmaActive ? 1 : 2;
  // Yes/No column and Interest Rate input size
  const smallInputColSize = 1;

  // Alternate asset rows use a single Bootstrap 12-column row. HOTMA plus the Ohio
  // Asset Verification column must stay within 12; extra width is redistributed so the row fills the grid.
  const requiresOhioAltAssetVerification =
    !!stateRules?.requiresAssetVerification;
  // Asset Verification uses 2 cols for readability; Annual Income and Actual/Impute use 1 each.
  // A dedicated trailing col (header + rows) keeps the grid at 12 columns (delete button or blank).
  const assetVerificationColSize = requiresOhioAltAssetVerification
    ? 2
    : largeInputColSize;
  const annualIncomeColSize =
    requiresOhioAltAssetVerification && !isBankDataRequired
      ? 1
      : largeInputColSize;
  const actualImputeColSize =
    requiresOhioAltAssetVerification && isHotmaActive ? 1 : largeInputColSize;

  const assetRowsProps = {
    fields,
    fieldsAnnualIncome,
    intl,
    assetThreshold,
    isBankDataRequired,
    isHotmaActive,
    hasAlternateColumns,
    stateRules,
    largeInputColSize,
    mediumInputColSize,
    smallInputColSize,
    assetVerificationColSize,
    annualIncomeColSize,
    actualImputeColSize,
    noClicked: hasAlternateColumns
      ? generateAltNoClicked(change)
      : generateNoClicked(change),
    isPrior: !!priorAndNotCompliance || isDisabled || isFormDisabled,
    handleAddAccount: createNewRow,
    calculateCashValue: generateCalcCashValueFunc(change, props),
    calculateAnnualIncome: generateCalcAnnualIncomeFunc(change, props),
  };

  return (
    <div className="row d-flex flex-column flex-column-fill overflow-hidden">
      <div className="col-xs-12 col-lg-12 d-flex flex-column flex-column-fill">
        <div className="panel block block-white-shadow d-flex flex-column flex-column-fill overflow-hidden">
          <div className="panel-head flex-shrink-0">
            {hotmaChanges && (
              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={2}
                sx={{ paddingBottom: '15px' }}
              >
                <Grid item>
                  <Banner
                    color="purple"
                    text={intl.formatMessage(
                      messages?.[
                        isHotmaActive
                          ? 'postHotmaFormNotification'
                          : 'preHotmaFormNotification'
                      ],
                    )}
                  />
                </Grid>
              </Grid>
            )}
            <div className="row">
              <div className="col-xs-10 col-md-11 col-lg-11">
                <h2>
                  <FormattedMessage {...messages.assetInformation} />{' '}
                  {applicantName}
                </h2>
                <NoPrint>
                  <div className="row">
                    <div className="col-xs-12">
                      <p className="no-margin-bottom">
                        <FormattedMessage {...messages.assetNote} />
                      </p>
                    </div>
                  </div>
                </NoPrint>
              </div>
              {!isPrior && (
                <div className="col-xs-2 col-md-1 col-lg-1 text-right">
                  <button
                    className="btn btn-tertiary btn-print no-print"
                    title="Print this form"
                    style={{ fontSize: '1.8em' }}
                    onClick={printAssetForm}
                    disabled={isSubmitting || isPrinting}
                  >
                    {isPrinting ? (
                      <Spinner small />
                    ) : (
                      <i className="icon et-print" />
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="panel-body d-flex flex-column flex-column-fill overflow-hidden">
            <form
              id="asset-information-form"
              className="d-flex flex-column flex-column-fill overflow-y-auto overflow-x-hidden"
              onSubmit={handleSubmit(submit)}
            >
              <div className="flex-column-fill overflow-auto">
                <div className="container-fluid minw-1400">
                  <div className="faux-table">
                    {hasAlternateColumns ? (
                      <AltAssetTableHeader
                        intl={intl}
                        isHotmaActive={isHotmaActive}
                        householdAssetThreshold={assetThreshold}
                        isBankDataRequired={isBankDataRequired}
                        stateRules={stateRules}
                        largeInputColSize={largeInputColSize}
                        mediumInputColSize={mediumInputColSize}
                        smallInputColSize={smallInputColSize}
                        assetVerificationColSize={assetVerificationColSize}
                        annualIncomeColSize={annualIncomeColSize}
                        actualImputeColSize={actualImputeColSize}
                      />
                    ) : (
                      <AssetTableHeader />
                    )}
                    <AlternateAssetFieldRow
                      assets={[
                        ...initialAssets,
                        ...(isHotmaActive && hasAlternateColumns
                          ? []
                          : retirementAccountsAssets),
                        ...lifeInsuranceAssets,
                        ...finalAssets,
                      ]}
                      {...assetRowsProps}
                      stateRules={stateRules}
                    />
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0">
                {hasAlternateColumns ? (
                  <AltAssetFooter
                    totalMarket={totalMarketAssets}
                    totalCash={totalCashValue}
                    totalAssetIncome={totalAssetIncome}
                    imputedIncome={imputedIncome}
                    isHotmaActive={isHotmaActive}
                    isBankDataRequired={isBankDataRequired}
                  />
                ) : (
                  <AssetFooter
                    totalAssets={totalMarketAssets}
                    totalAssetIncome={totalAssetIncome}
                    imputedIncome={imputedIncome}
                  />
                )}
              </div>
              {!isPrior || hasCompliancePermission ? (
                <Grid
                  className="flex-shrink-0"
                  container
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  spacing={4}
                >
                  {/* Product asked for the confirmation checkbox to always show
                      (FES-548). It is a hard gate on Save when the backend requires
                      assetConfirmation: HOTMA or auto-rollover programs, or every
                      program once the require-income-asset-confirmation flag is on
                      (FES-559). The Save button below applies requiresAssetConfirmation.
                      Where it is not required the checkbox is optional, so toggling it
                      never blocks Save. */}
                  <Grid item>
                    <CheckBox
                      label={intl.formatMessage(messages.assetConfirmation)}
                      name={'assetConfirmation'}
                      onChange={() =>
                        setIsAssetFormReviewed(!isAssetFormReviewed)
                      }
                      checked={isAssetFormReviewed}
                      disabled={
                        !valid ||
                        isDisabled ||
                        isSubmitting ||
                        hasSubmitted ||
                        priorAndNotCompliance ||
                        isFormDisabled
                      }
                    />
                  </Grid>
                  <Grid item>
                    <button
                      className="btn btn-primary center-block padbottom20"
                      disabled={
                        !valid ||
                        (requiresAssetConfirmation(
                          isHotmaActive,
                          autoRolloverIncomeAndAssets,
                          requireIncomeAssetConfirmation,
                        ) &&
                          !isAssetFormReviewed) ||
                        isDisabled ||
                        isSubmitting ||
                        hasSubmitted ||
                        priorAndNotCompliance ||
                        isFormDisabled
                      }
                    >
                      <FormattedMessage {...messages.saveAssetInformation} />
                    </button>
                  </Grid>
                </Grid>
              ) : null}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

const generateSelectorsforAlternateForm = (selector, form) => {
  const assetDefinitions = getAssetDefinitions();
  return assetDefinitions.reduce((mp, assetDef) => {
    const isExpandableAsset = assetDef.expandable;
    const assetName = assetDef.name;
    if (!isExpandableAsset) {
      return {
        ...mp,
        [`${assetName}AssetMarketValue`]: selector(
          { form },
          `${assetName}AssetMarketValue`,
        ),
        [`${assetName}AssetInterestRate`]: selector(
          { form },
          `${assetName}AssetInterestRate`,
        ),
        [`${assetName}AssetFeesToConvertCash`]: selector(
          { form },
          `${assetName}AssetFeesToConvertCash`,
        ),
        [`${assetName}AssetCashValue`]: selector(
          { form },
          `${assetName}AssetCashValue`,
        ),
        [`${assetName}AssetAnnualIncome`]: selector(
          { form },
          `${assetName}AssetAnnualIncome`,
        ),
        [`${assetName}AssetActualImpute`]: selector(
          { form },
          `${assetName}AssetActualImpute`,
        ),
        [`${assetName}AssetBankSource`]: selector(
          { form },
          `${assetName}AssetBankSource`,
        ),
        [`${assetName}AssetAccountNumber`]: selector(
          { form },
          `${assetName}AssetAccountNumber`,
        ),
      };
    } else {
      return {
        ...mp,
        [`${assetName}List`]: selector({ form }, `${assetName}List`),
      };
    }
  }, {});
};

export const mapStateToProps = (
  { form }: Object,
  { initialValues }: Object,
): Object => {
  const selector = formValueSelector('assetInformation');
  return {
    initialValues,
    cashHeldAssetIncome: selector({ form }, 'cashHeldAssetIncome'),
    cashHeldAssetValue: selector({ form }, 'cashHeldAssetValue'),
    certificateOfDepositAssetIncome: selector(
      { form },
      'certificateOfDepositAssetIncome',
    ),
    certificateOfDepositAssetValue: selector(
      { form },
      'certificateOfDepositAssetValue',
    ),
    checkingAccountAssetIncome: selector(
      { form },
      'checkingAccountAssetIncome',
    ),
    checkingAccountAssetValue: selector({ form }, 'checkingAccountAssetValue'),
    disposedAssetIncome: selector({ form }, 'disposedAssetIncome'),
    disposedAssetValue: selector({ form }, 'disposedAssetValue'),
    investmentPropertiesAssetIncome: selector(
      { form },
      'investmentPropertiesAssetIncome',
    ),
    investmentPropertiesAssetValue: selector(
      { form },
      'investmentPropertiesAssetValue',
    ),
    iraAssetIncome: selector({ form }, 'iraAssetIncome'),
    iraAssetValue: selector({ form }, 'iraAssetValue'),
    k401AssetIncome: selector({ form }, 'k401AssetIncome'),
    k401AssetValue: selector({ form }, 'k401AssetValue'),
    keoghAssetIncome: selector({ form }, 'keoghAssetIncome'),
    keoghAssetValue: selector({ form }, 'keoghAssetValue'),
    otherRetirementAccountAssetIncome: selector(
      { form },
      'otherRetirementAccountAssetIncome',
    ),
    otherRetirementAccountAssetValue: selector(
      { form },
      'otherRetirementAccountAssetValue',
    ),
    lifeInsuranceAssetIncome: selector({ form }, 'lifeInsuranceAssetIncome'),
    lifeInsuranceAssetValue: selector({ form }, 'lifeInsuranceAssetValue'),
    otherAssetIncome: selector({ form }, 'otherAssetIncome'),
    otherAssetValue: selector({ form }, 'otherAssetValue'),
    payCardAssetIncome: selector({ form }, 'payCardAssetIncome'),
    payCardAssetValue: selector({ form }, 'payCardAssetValue'),
    pensionAssetIncome: selector({ form }, 'pensionAssetIncome'),
    pensionAssetValue: selector({ form }, 'pensionAssetValue'),
    realEstateAssetIncome: selector({ form }, 'realEstateAssetIncome'),
    realEstateAssetValue: selector({ form }, 'realEstateAssetValue'),
    rentalPropertyAssetIncome: selector({ form }, 'rentalPropertyAssetIncome'),
    rentalPropertyAssetValue: selector({ form }, 'rentalPropertyAssetValue'),
    savingsAccountAssetIncome: selector({ form }, 'savingsAccountAssetIncome'),
    savingsAccountAssetValue: selector({ form }, 'savingsAccountAssetValue'),
    stocksOrBondsAssetIncome: selector({ form }, 'stocksOrBondsAssetIncome'),
    stocksOrBondsAssetValue: selector({ form }, 'stocksOrBondsAssetValue'),
    treasuryMoneyMarketFundAssetIncome: selector(
      { form },
      'treasuryMoneyMarketFundAssetIncome',
    ),
    treasuryMoneyMarketFundAssetValue: selector(
      { form },
      'treasuryMoneyMarketFundAssetValue',
    ),
    trustAccountAssetIncome: selector({ form }, 'trustAccountAssetIncome'),
    trustAccountAssetValue: selector({ form }, 'trustAccountAssetValue'),
    cashHeld: selector({ form }, 'cashHeld'),
    certificateOfDeposit: selector({ form }, 'certificateOfDeposit'),
    checkingAccount: selector({ form }, 'checkingAccount'),
    disposed: selector({ form }, 'disposed'),
    investmentProperties: selector({ form }, 'investmentProperties'),
    ira: selector({ form }, 'ira'),
    k401: selector({ form }, 'k401'),
    keogh: selector({ form }, 'keogh'),
    otherRetirementAccountName: selector(
      { form },
      'otherRetirementAccountName',
    ),
    otherRetirementAccount: selector({ form }, 'otherRetirementAccount'),
    lifeInsurance: selector({ form }, 'lifeInsurance'),
    otherAssetName: selector({ form }, 'otherAssetName'),
    other: selector({ form }, 'other'),
    payCard: selector({ form }, 'payCard'),
    pension: selector({ form }, 'pension'),
    realEstate: selector({ form }, 'realEstate'),
    rentalProperty: selector({ form }, 'rentalProperty'),
    savingsAccount: selector({ form }, 'savingsAccount'),
    federalTaxRefundOrCredit: selector({ form }, 'federalTaxRefundOrCredit'),
    stocksOrBonds: selector({ form }, 'stocksOrBonds'),
    treasuryMoneyMarketFund: selector({ form }, 'treasuryMoneyMarketFund'),
    trustAccount: selector({ form }, 'trustAccount'),
    assetConfirmation: selector({ form }, 'assetConfirmation'),
    // For alterate form calculations
    ...generateSelectorsforAlternateForm(selector, form),
  };
};

export default withRouter(
  connect(mapStateToProps)(
    reduxForm({
      form: 'assetInformation',
      validate,
      enableReinitialize: true,
    })(AssetInformation),
  ),
);
